import React, { useCallback, useEffect, useState } from 'react';
import { AppStateUser } from '../../../client/providers/AppProvider';
import { CloseIcon } from '../../svg/CloseIcon';
import { SendIcon } from '../../svg/dashboard/messages/SendIcon';
import { ConversationNavigation } from './ConversationNavigation';
import { gql } from '@urql/core';
import {
    Espin_Role_Enum,
    SelectedConversationContentFragment,
    CreateMessageMutation,
    CreateMessageMutationVariables,
    CreateMessageDocument,
    Espin_Message_Type_Enum,
} from '../../../generated/spin-graphql';
import { CollegeSelectedConversation } from './colleges/CollegeSelectedConversation';
import { PlayerSelectedConversation } from './players/PlayerSelectedConversation';
import { useAuthenticatedMutation } from '../../../client/hooks';
import { useRouter } from 'next/router';
import { AdminSelectedConversation } from './admin/AdminSelectedConversation';
import { HighSchoolSelectedConversation } from './high-school/HighSchoolSelectedConversation';
import { getMessageType } from './InitialConversationContent';

type Props = {
    user: AppStateUser;
    onBackClicked: () => void;
    selectedConversation: SelectedConversationContentFragment[];
    adminMessages: SelectedConversationContentFragment[];
};

gql`
    fragment SelectedConversationContent on espin_message {
        player_user_id
        college_id
        parent_user_id
        college_user_id
        ...CollegeSelectedConversation
        ...PlayerSelectedConversation
    }

    mutation CreateMessage($input: espin_message_insert_input!) {
        insert_espin_message_one(object: $input, on_conflict: { constraint: message_pkey, update_columns: [] }) {
            id
        }
    }
`;

function getConversantName(conversation: SelectedConversationContentFragment, user: AppStateUser) {
    if (user.role === Espin_Role_Enum.EspinCollegeUser) {
        if (conversation.player_user) {
            return `${conversation.player_user?.player_profile?.first_name} ${conversation.player_user?.player_profile?.last_name}`;
        } else if (conversation.high_school) {
            return conversation.high_school.name;
        } else if (!!conversation.player_user && conversation.player_user.deleted_at) {
            return 'Deleted User';
        }
    }
    return conversation.college?.name;
}

export const SelectedConversationContent: React.FC<Props> = ({
    user,
    onBackClicked,
    selectedConversation,
    adminMessages,
}) => {
    const [selectedMessage, setSelectedMessage] = useState<string>(undefined);
    const [lockedConversationNavigation, setLockedConversationNavigation] = useState(false);
    const router = useRouter();
    const conversantName = getConversantName(selectedConversation[0], user);
    const { player_user_id, college_id, parent_user_id, college_user_id, high_school } = selectedConversation[0];

    const [result, createMessageMutation] = useAuthenticatedMutation<
        CreateMessageMutation,
        CreateMessageMutationVariables
    >(CreateMessageDocument);

    useEffect(() => {
        const handleRouteChange = () => {
            setSelectedMessage(undefined);
        };
        router.events.on('routeChangeStart', handleRouteChange);
    }, [router.events]);

    useEffect(() => {
        // check if logged in user sent 3 messages in a row, and lock conversation navigation if so
        setLockedConversationNavigation(false);
        if (selectedConversation.length >= 3) {
            if (user.role === Espin_Role_Enum.EspinCollegeUser) {
                if (
                    selectedConversation[selectedConversation.length - 1].message_type ===
                        Espin_Message_Type_Enum.CollegeMessagesPlayer &&
                    selectedConversation[selectedConversation.length - 2].message_type ===
                        Espin_Message_Type_Enum.CollegeMessagesPlayer &&
                    selectedConversation[selectedConversation.length - 3].message_type ===
                        Espin_Message_Type_Enum.CollegeMessagesPlayer
                ) {
                    setLockedConversationNavigation(true);
                }
            } else {
                if (
                    selectedConversation[selectedConversation.length - 1].message_type ===
                        Espin_Message_Type_Enum.PlayerMessagesCollege &&
                    selectedConversation[selectedConversation.length - 2].message_type ===
                        Espin_Message_Type_Enum.PlayerMessagesCollege &&
                    selectedConversation[selectedConversation.length - 3].message_type ===
                        Espin_Message_Type_Enum.PlayerMessagesCollege
                ) {
                    setLockedConversationNavigation(true);
                }
            }
        }
    }, [selectedConversation, user.role]);

    const sendMessage = useCallback(async () => {
        const result = await createMessageMutation({
            input: {
                player_user_id,
                college_id,
                ...(user.role === Espin_Role_Enum.EspinParentUser && { parent_user_id: user.id }),
                // fix for the case where player starts the conversation here
                college_user_id: user.role === Espin_Role_Enum.EspinCollegeUser ? user.id : college_user_id,
                high_school_user_id: user.role === Espin_Role_Enum.EspinHighschoolUser ? user.id : undefined,
                high_school_id: high_school?.id,
                message_type: getMessageType(user, !!high_school?.id),
                contents: selectedMessage,
            },
        });

        if (result.error) {
            console.error(result.error);
            return;
        }
        setSelectedMessage(undefined);
    }, [user, high_school, createMessageMutation, player_user_id, college_id, college_user_id, selectedMessage]);

    const adminConversationSelected = adminMessages.length > 0 && router.query.show_admin_messages === 'true';

    return (
        <>
            {adminConversationSelected && (
                <AdminSelectedConversation
                    onBackClicked={onBackClicked}
                    selectedConversation={{
                        nameLabel: 'Stay Plugged In',
                        messages: adminMessages.map((m) => {
                            return { message: m.contents, date: m.created_at, playerUserId: m.player_user_id };
                        }),
                    }}
                />
            )}
            {!adminConversationSelected && user.role === Espin_Role_Enum.EspinCollegeUser && (
                <CollegeSelectedConversation
                    onBackClicked={onBackClicked}
                    selectedConversation={selectedConversation}
                />
            )}
            {!adminConversationSelected &&
                (user.role === Espin_Role_Enum.EspinParentUser || user.role === Espin_Role_Enum.EspinPlayerUser) && (
                    <PlayerSelectedConversation
                        onBackClicked={onBackClicked}
                        selectedConversation={selectedConversation}
                    />
                )}

            {!adminConversationSelected && user.role === Espin_Role_Enum.EspinHighschoolUser && (
                <HighSchoolSelectedConversation
                    onBackClicked={onBackClicked}
                    selectedConversation={selectedConversation}
                />
            )}
            {!adminConversationSelected && (
                <div className="conversation-controls">
                    {!!selectedMessage ? (
                        <>
                            <div className="selected-message">
                                {selectedMessage}
                                <button className="button close-button" onClick={() => setSelectedMessage(undefined)}>
                                    <CloseIcon />
                                </button>
                            </div>
                            <button className="button circle-button send-button" onClick={() => sendMessage()}>
                                <SendIcon />
                            </button>
                        </>
                    ) : (
                        <ConversationNavigation
                            isLocked={lockedConversationNavigation}
                            user={user}
                            conversantName={conversantName}
                            setSelectedMessage={(message) => setSelectedMessage(message)}
                        />
                    )}
                </div>
            )}
        </>
    );
};
